/**
 * The few marks the shell needs, drawn rather than typed.
 *
 * Glyphs like ☰ and ▤ are text: they change with the font, some of them are
 * missing on Android, and the lint rule that keeps user-visible strings in the
 * locale files cannot tell them from words. An inline SVG is none of those
 * things and costs nothing.
 *
 * `currentColor` throughout, so an icon is coloured by whatever contains it and
 * follows the theme without being told about it.
 */

interface Props {
  className?: string;
}

function frame(children: React.ReactNode, className?: string) {
  return (
    <svg
      className={className}
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

export function MenuIcon({ className }: Props) {
  return frame(
    <>
      <path d="M4 7h16" />
      <path d="M4 12h16" />
      <path d="M4 17h16" />
    </>,
    className,
  );
}

/** A barcode under a reading frame — the same picture on the button and on the
 *  phone's bar, so that one is recognisably the other. */
export function ScanIcon({ className }: Props) {
  return frame(
    <>
      <path d="M4 8V6a2 2 0 0 1 2-2h2" />
      <path d="M16 4h2a2 2 0 0 1 2 2v2" />
      <path d="M20 16v2a2 2 0 0 1-2 2h-2" />
      <path d="M8 20H6a2 2 0 0 1-2-2v-2" />
      <path d="M8 9v6" />
      <path d="M11.5 9v6" />
      <path d="M15 9v6" />
    </>,
    className,
  );
}

export function ScaleIcon({ className }: Props) {
  return frame(
    <>
      <path d="M12 4v16" />
      <path d="M7 20h10" />
      <path d="M5 7h14" />
      <path d="M6 7l-3 6h6L6 7Z" />
      <path d="M18 7l-3 6h6l-3-6Z" />
    </>,
    className,
  );
}

export function ChevronLeftIcon({ className }: Props) {
  return frame(<path d="M14 6l-6 6 6 6" />, className);
}

export function ChevronRightIcon({ className }: Props) {
  return frame(<path d="M10 6l6 6-6 6" />, className);
}

export function ChevronDownIcon({ className }: Props) {
  return frame(<path d="M6 10l6 6 6-6" />, className);
}

/** A sheet with an arrow leaving it: take this table away as a file. */
export function ExportIcon({ className }: Props) {
  return frame(
    <>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-9" />
      <path d="M14 3v5h5" />
      <path d="M12 12v6M9 15l3 3 3-3" />
    </>,
    className,
  );
}

export function PrintIcon({ className }: Props) {
  return frame(
    <>
      <path d="M7 9V4h10v5" />
      <rect x="4" y="9" width="16" height="7" rx="1" />
      <path d="M7 14h10v6H7z" />
    </>,
    className,
  );
}

/** A pencil for compact edit actions where the surrounding row already says
 * what is being changed. */
export function EditIcon({ className }: Props) {
  return frame(
    <>
      <path d="m14.5 5.5 4 4" />
      <path d="M4 20l3.6-.8L19.3 7.5a2.8 2.8 0 0 0-4-4L3.6 15.2 4 20Z" />
    </>,
    className,
  );
}

/** A funnel. Filled in when the column is actually filtering something. */
export function FilterIcon({ className }: Props) {
  return frame(<path d="M4 5h16l-6 7v6l-4 2v-8L4 5z" />, className);
}

/** The three states of the theme switch: follow the machine, day, night. */
export function MonitorIcon({ className }: Props) {
  return frame(
    <>
      <rect x="3" y="4" width="18" height="12" rx="2" />
      <path d="M8 20h8M12 16v4" />
    </>,
    className,
  );
}

export function SunIcon({ className }: Props) {
  return frame(
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M19.1 4.9l-1.4 1.4M6.3 17.7l-1.4 1.4" />
    </>,
    className,
  );
}

export function MoonIcon({ className }: Props) {
  return frame(<path d="M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5z" />, className);
}

/** An arrow leaving a box: this opens somewhere else. */
export function ExternalLinkIcon({ className }: Props) {
  return frame(
    <>
      <path d="M14 4h6v6" />
      <path d="M20 4l-9 9" />
      <path d="M19 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5" />
    </>,
    className,
  );
}

/** A parcel on the move: where is it now? */
export function TruckIcon({ className }: Props) {
  return frame(
    <>
      <path d="M3 7h10v9H3zM13 10h4l3 3v3h-7z" />
      <circle cx="7" cy="18" r="1.6" />
      <circle cx="17" cy="18" r="1.6" />
    </>,
    className,
  );
}

export function PlusIcon({ className }: Props) {
  return frame(<path d="M12 5v14M5 12h14" />, className);
}

export function ClipboardImageIcon({ className }: Props) {
  return frame(
    <>
      <rect x="7" y="5" width="12" height="15" rx="2" />
      <path d="M9 5V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v1" />
      <circle cx="11" cy="11" r="1" />
      <path d="m9 17 3-3 2 2 2-2 2 3" />
    </>,
    className,
  );
}

/** Two arrows, up and down: put these in the order you want them. */
export function ReorderIcon({ className }: Props) {
  return frame(
    <>
      <path d="M8 4v16M8 4L5 7M8 4l3 3" />
      <path d="M16 20V4M16 20l-3-3M16 20l3-3" />
    </>,
    className,
  );
}

/** A tick: "yes, this one", "apply". */
export function CheckIcon({ className }: Props) {
  return frame(<path d="M5 13l4 4L19 7" />, className);
}

/** A cross: "clear what is written here". Not a close button — that is the
 *  press outside, and every screen here already treats it as one. */
export function CrossIcon({ className }: Props) {
  return frame(<path d="M6 6l12 12M18 6L6 18" />, className);
}

export function UserIcon({ className }: Props) {
  return frame(
    <>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 20a7 7 0 0 1 14 0" />
    </>,
    className,
  );
}

/** A door with an arrow leaving through it. */
export function SignOutIcon({ className }: Props) {
  return frame(
    <>
      <path d="M14 4H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h7" />
      <path d="M17 8l4 4-4 4M21 12h-9" />
    </>,
    className,
  );
}

/**
 * The mark for each section of the menu.
 *
 * A collapsed panel is four centimetres of nothing without these, and on a
 * phone an icon beside the word is what makes the row findable at a glance
 * rather than read word by word. Keyed by the same section name the labels are,
 * so adding a section that has no picture yet degrades to a blank rather than a
 * crash.
 */
const SECTION_PATHS: Record<string, React.ReactNode> = {
  // A chip with legs.
  components: (
    <>
      <rect x="8" y="8" width="8" height="8" rx="1" />
      <path d="M10 8V5M14 8V5M10 19v-3M14 19v-3M8 10H5M8 14H5M19 10h-3M19 14h-3" />
    </>
  ),
  // Drawers in a cabinet.
  locations: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="2" />
      <path d="M4 12h16M10 8h4M10 16h4" />
    </>
  ),
  // Boxes on a pallet.
  stock: (
    <>
      <path d="M3 8l9-4 9 4-9 4-9-4z" />
      <path d="M3 12l9 4 9-4M3 16l9 4 9-4" />
    </>
  ),
  // A counting sheet with a completed line.
  stocktakes: (
    <>
      <rect x="5" y="3" width="14" height="18" rx="2" />
      <path d="M9 3.5h6v3H9zM8 11l2 2 4-4M8 17h8" />
    </>
  ),
  // A parcel.
  purchases: (
    <>
      <path d="M4 8l8-4 8 4v8l-8 4-8-4V8z" />
      <path d="M4 8l8 4 8-4M12 12v8" />
    </>
  ),
  // A document with lines.
  specifications: (
    <>
      <path d="M6 3h8l4 4v14H6z" />
      <path d="M14 3v4h4M9 12h6M9 16h6" />
    </>
  ),
  // A board with a route on it.
  projects: (
    <>
      <path d="M4 7h6M14 7h6M4 17h6M14 17h6" />
      <circle cx="12" cy="7" r="2" />
      <circle cx="12" cy="17" r="2" />
      <path d="M12 9v6" />
    </>
  ),
  // A printer with paper coming out.
  station: (
    <>
      <path d="M7 9V4h10v5" />
      <rect x="4" y="9" width="16" height="7" rx="1" />
      <path d="M7 14h10v6H7z" />
    </>
  ),
  // A sheet going into a tray.
  imports: (
    <>
      <path d="M12 3v10M8 9l4 4 4-4" />
      <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
    </>
  ),
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3" />
      <path d="M3 20a6 6 0 0 1 12 0" />
      <path d="M16 5.5a3 3 0 0 1 0 5M17 14a6 6 0 0 1 4 6" />
    </>
  ),
  // A log with a tick.
  audit: (
    <>
      <path d="M5 4h11l3 3v13H5z" />
      <path d="M9 12l2 2 4-4" />
    </>
  ),
  // A drum, as on every backup button ever drawn.
  backups: (
    <>
      <ellipse cx="12" cy="6" rx="7" ry="3" />
      <path d="M5 6v12c0 1.7 3.1 3 7 3s7-1.3 7-3V6" />
      <path d="M5 12c0 1.7 3.1 3 7 3s7-1.3 7-3" />
    </>
  ),
  // A phone with an arrow coming down into it: the build this site hands out.
  release: (
    <>
      <rect x="6" y="2.5" width="12" height="19" rx="2.5" />
      <path d="M12 7v6M9.5 10.5l2.5 2.5 2.5-2.5" />
      <path d="M9.5 18h5" />
    </>
  ),
  security: (
    <>
      <path d="M12 3l7 3v6c0 4.4-3 8-7 9-4-1-7-4.6-7-9V6l7-3z" />
      <path d="M9 12l2 2 4-4" />
    </>
  ),
};

export function SectionIcon({ name, className }: Props & { name: string }) {
  const drawing = SECTION_PATHS[name];
  return drawing ? frame(drawing, className) : null;
}

/**
 * What kind of place this is, as a picture.
 *
 * A tree of a hundred names all set in the same type is a wall of text; the
 * shape of the row is what tells a shelf from the cabinet holding it before a
 * single word is read.
 */
const PLACE_PATHS: Record<string, React.ReactNode> = {
  room: (
    <>
      <path d="M3 11l9-7 9 7" />
      <path d="M5 10v10h14V10" />
      <path d="M10 20v-6h4v6" />
    </>
  ),
  cabinet: (
    <>
      <rect x="5" y="3" width="14" height="18" rx="1" />
      <path d="M5 12h14M11 8v2M11 15v2" />
    </>
  ),
  shelf: (
    <>
      <path d="M4 5h16M4 12h16M4 19h16" />
      <path d="M6 5v14M18 5v14" />
    </>
  ),
  box: (
    <>
      <path d="M4 8l8-4 8 4v8l-8 4-8-4V8z" />
      <path d="M4 8l8 4 8-4M12 12v8" />
    </>
  ),
  compartment: (
    <>
      <rect x="4" y="6" width="16" height="12" rx="1" />
      <path d="M12 6v12" />
    </>
  ),
};

export function PlaceIcon({ name, className }: Props & { name: string }) {
  const drawing = PLACE_PATHS[name];
  return drawing ? frame(drawing, className) : frame(<circle cx="12" cy="12" r="7" />, className);
}
